/**
 * Fenêtres flottantes du CRM — barre des tâches façon Windows.
 *
 * Deux panneaux vivent au-dessus des pages pendant un appel : la préparation
 * (« Ma préparation ») et le copilote. Ils partagent ce châssis :
 *
 *   - un en-tête qui ne défile pas, avec réduire / agrandir / fermer, et qui
 *     se saisit à la souris pour déplacer la fenêtre ;
 *   - chaque fenêtre montée a son **onglet dans la barre du softphone** — la
 *     seule barre permanente de l'application, donc l'endroit où l'on va
 *     rechercher ce qu'on a rangé. L'onglet est « actif » quand la fenêtre est
 *     déployée, « rangé » quand elle est réduite : un clic reprend une fenêtre
 *     rangée (ou réduit une fenêtre déployée), glisser un onglet change
 *     l'ordre. Le fournisseur tient donc un tableau ORDONNÉ de fenêtres, et
 *     la barre (`FloatingWindowDock`) le dessine ;
 *   - fermer reste distinct de réduire : l'un range, l'autre retire ;
 *   - **une seule fenêtre déployée sous 640 px.** Deux panneaux ne tiennent
 *     pas côte à côte sur un écran de 360 px : en reprendre une range l'autre,
 *     plutôt que de les empiler l'une sur l'autre.
 *
 * Le montage se fait dans <body> (portail), comme la modale et pour la même
 * raison : un ancêtre transformé deviendrait le bloc conteneur des `fixed`, et
 * la fenêtre se calerait sur la hauteur de la page au lieu de l'écran.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { createPortal } from "react-dom";
import { Minus, X, Maximize2, Minimize2, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

const SOLO_QUERY = "(max-width: 639px)";

// Teinte de l'icône d'une fenêtre : tuile claire, lisible aussi bien dans
// l'en-tête blanc que sur la barre sombre.
export const WINDOW_TONES = {
  blue: { bg: "#e8f1fd", fg: "#005bd3" },
  violet: { bg: "#f3e8ff", fg: "#7c3aed" },
  green: { bg: "#e7f5f1", fg: "#008060" },
  amber: { bg: "#fdf3e7", fg: "#8a6116" },
  slate: { bg: "#f2f4f7", fg: "#4a5261" },
};
const toneOf = (tone) => WINDOW_TONES[tone] || WINDOW_TONES.slate;

// Repli pour les appelants qui ne passent qu'une classe de couleur `accent`
// (text-indigo-600, text-[#2f9e5c]…) : on en déduit la teinte.
function toneFromAccent(accent) {
  const a = String(accent || "");
  if (/indigo|blue|sky|005bd3/i.test(a)) return "blue";
  if (/emerald|green|2f9e5c|008060/i.test(a)) return "green";
  if (/amber|orange|yellow/i.test(a)) return "amber";
  if (/violet|purple|fuchsia/i.test(a)) return "violet";
  return "slate";
}

const FloatingCtx = createContext(null);
/** Accès au registre des fenêtres (la barre du softphone s'en sert pour
 *  dimensionner ses onglets). `null` hors fournisseur. */
export const useFloatingWindows = () => useContext(FloatingCtx);

const isSolo = () => typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia(SOLO_QUERY).matches;

const META_KEYS = ["title", "note", "icon", "tone", "testid", "closeTitle"];
const sameMeta = (a, b) => META_KEYS.every((k) => a[k] === b[k]);

export function FloatingWindowsProvider({ children }) {
  // Tableau ORDONNÉ : { id, minimized, title, note, icon, tone, testid,
  // closeTitle }. L'ordre est celui des onglets de la barre — il se réarrange
  // à la souris, d'où un tableau et non un objet id -> état.
  const [windows, setWindows] = useState([]);
  // Nœud de la barre des tâches, publié dans le contexte une fois monté. Le
  // passer par l'état plutôt que d'aller le chercher par son id évite de lire
  // le DOM pendant un rendu.
  const [dock, setDock] = useState(null);
  // Rappels (fermer) tenus hors de l'état : ils changent d'identité à chaque
  // rendu de la fenêtre, les mettre dans l'état relancerait les effets en
  // boucle.
  const handlers = useRef({});

  // Ces fonctions doivent garder leur identité d'un rendu à l'autre : les
  // fenêtres s'enregistrent dans un effet qui en dépend.
  const restore = useCallback((id) => setWindows((list) => {
    const solo = isSolo();
    return list.map((w) => (w.id === id ? { ...w, minimized: false }
      : solo ? { ...w, minimized: true } : w));
  }), []);

  const register = useCallback((id, meta) => setWindows((list) => {
    const idx = list.findIndex((w) => w.id === id);
    if (idx >= 0) {
      if (sameMeta(list[idx], meta)) return list;
      const next = [...list];
      next[idx] = { ...list[idx], ...meta };
      return next;
    }
    // Une fenêtre qui se monte se déploie ; sur téléphone elle range les autres.
    const others = isSolo() ? list.map((w) => ({ ...w, minimized: true })) : list;
    return [...others, { id, minimized: false, ...meta }];
  }), []);

  const unregister = useCallback((id) => {
    delete handlers.current[id];
    setWindows((list) => (list.some((w) => w.id === id) ? list.filter((w) => w.id !== id) : list));
  }, []);
  const minimize = useCallback(
    (id) => setWindows((list) => list.map((w) => (w.id === id ? { ...w, minimized: true } : w))), []);
  const minimizeAll = useCallback(
    () => setWindows((list) => (list.some((w) => !w.minimized)
      ? list.map((w) => ({ ...w, minimized: true })) : list)), []);
  const move = useCallback((from, to) => setWindows((list) => {
    if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
    const next = [...list];
    const [w] = next.splice(from, 1);
    next.splice(to, 0, w);
    return next;
  }), []);
  const setHandlers = useCallback((id, h) => { handlers.current[id] = h; }, []);
  const close = useCallback((id) => { handlers.current[id]?.onClose?.(); }, []);

  const api = useMemo(
    () => ({ windows, dock, setDock, register, unregister, minimize, minimizeAll, restore, move, setHandlers, close }),
    [windows, dock, register, unregister, minimize, minimizeAll, restore, move, setHandlers, close]);

  return <FloatingCtx.Provider value={api}>{children}</FloatingCtx.Provider>;
}

const DOCK_CLOSE_BTN = "flex h-[26px] w-[26px] flex-none items-center justify-center rounded-md text-[#9aa0ab] transition-colors hover:bg-[#e81123] hover:text-white";

/** Un onglet de la barre des tâches. */
function DockTab({ w, iconOnly, showLabel, dragging, onActivate, onClose, ...drag }) {
  const tone = toneOf(w.tone);
  const Icon = w.icon;
  const active = !w.minimized;
  const tid = w.testid;
  return (
    <div data-testid={tid ? `${tid}-pill` : undefined} data-active={active ? "true" : "false"}
      title={w.title} draggable {...drag}
      className={cn(
        "relative flex h-10 max-w-[230px] flex-none cursor-grab items-center gap-2 rounded-lg border transition-colors",
        iconOnly && !showLabel ? "px-1.5" : "pl-2.5 pr-1",
        active ? "border-white/[.16] bg-white/[.14]" : "border-transparent hover:bg-white/10",
        dragging && "opacity-[.45]",
      )}>
      <button type="button" onClick={onActivate}
        title={active ? `Réduire — ${w.title}` : `Reprendre — ${w.title}`}
        data-testid={tid ? `${tid}-restore` : undefined}
        className="flex min-w-0 items-center gap-2 text-left">
        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md"
          style={{ background: tone.bg, color: tone.fg }}>
          {Icon && <Icon size={15} />}
        </span>
        {showLabel && (
          <span className="min-w-0 truncate text-[13px] font-medium text-[#eceef2]">{w.title}</span>
        )}
        {!iconOnly && w.note && (
          <span className="max-w-[40%] flex-none truncate font-mono text-[11px] text-[#9aa0ab]">{w.note}</span>
        )}
      </button>
      {!iconOnly && (
        <button type="button" onClick={onClose} title={w.closeTitle || "Fermer"}
          aria-label={w.closeTitle || "Fermer"} className={DOCK_CLOSE_BTN}>
          <X size={13} />
        </button>
      )}
      {/* Indicateur collé en bas : long et bleu pour la fenêtre déployée,
          court et gris pour une fenêtre rangée. */}
      <span className={cn("absolute -bottom-px left-1/2 h-[3px] -translate-x-1/2 rounded-sm",
        active ? "w-5 bg-[#4f9dff]" : "w-2 bg-[#6b7280]")} />
    </div>
  );
}

/**
 * La barre des tâches, à poser dans la barre du bas.
 *
 * Dessine un onglet par fenêtre montée (déployée ou rangée), dans l'ordre du
 * registre. `iconOnly` / `maxVisible` / `labelActive` viennent des règles
 * d'adaptation de la barre du softphone ; par défaut tout s'affiche.
 * `onActivate(id)` est appelé quand une fenêtre est reprise — la barre s'en
 * sert pour refermer le composeur (une seule surface ouverte à la fois).
 *
 * Le nœud reste monté même vide mais se replie alors (`hidden`), sans quoi il
 * ouvrirait un espace dans la barre.
 */
export function FloatingWindowDock({
  className, iconOnly = false, maxVisible = Infinity, labelActive = true, onActivate,
}) {
  const ctx = useContext(FloatingCtx);
  const [dragIdx, setDragIdx] = useState(null);
  const [more, setMore] = useState(false);
  const moreRef = useRef(null);
  const windows = ctx?.windows || [];
  const visible = Number.isFinite(maxVisible) ? windows.slice(0, Math.max(0, maxVisible)) : windows;
  const hidden = windows.slice(visible.length);

  // La liste de débordement se referme d'un clic ailleurs, et d'elle-même
  // quand plus rien n'y figure.
  useEffect(() => {
    if (!more) return undefined;
    const onDown = (e) => { if (!moreRef.current?.contains(e.target)) setMore(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [more]);
  useEffect(() => { if (!hidden.length) setMore(false); }, [hidden.length]);

  if (!ctx) return null;

  const activate = (w) => {
    if (w.minimized) { ctx.restore(w.id); onActivate?.(w.id); } else ctx.minimize(w.id);
    setMore(false);
  };

  return (
    <div ref={ctx.setDock} data-testid="floating-dock"
      className={cn("flex min-w-0 items-center gap-1", windows.length === 0 && "hidden", className)}>
      {visible.map((w, i) => (
        <DockTab key={w.id} w={w} iconOnly={iconOnly} dragging={dragIdx === i}
          showLabel={!iconOnly || (labelActive && !w.minimized)}
          onActivate={() => activate(w)} onClose={() => ctx.close(w.id)}
          // Réordonnancement HTML5 : la liste se réorganise en direct pendant
          // le survol, l'onglet source reste estompé jusqu'au dépôt.
          onDragStart={(e) => {
            if (e.dataTransfer) {
              e.dataTransfer.effectAllowed = "move";
              try { e.dataTransfer.setData("text/plain", String(i)); } catch { /* navigateur strict */ }
            }
            setDragIdx(i);
          }}
          onDragOver={(e) => {
            if (dragIdx == null || dragIdx === i) return;
            e.preventDefault();
            ctx.move(dragIdx, i);
            setDragIdx(i);
          }}
          onDrop={(e) => { e.preventDefault(); setDragIdx(null); }}
          onDragEnd={() => setDragIdx(null)} />
      ))}
      {hidden.length > 0 && (
        <div ref={moreRef} className="relative flex-none">
          <button type="button" onClick={() => setMore((m) => !m)} title="Autres fenêtres"
            aria-expanded={more} data-testid="floating-dock-more"
            className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-white/[.14] bg-white/[.06] px-2.5 text-[13px] font-semibold text-[#e6e8ec] transition-colors hover:bg-white/[.12]">
            <ChevronUp size={14} /> {hidden.length}
          </button>
          {more && (
            <div data-testid="floating-dock-overflow"
              className="absolute bottom-12 right-0 z-50 flex w-[min(280px,80vw)] flex-col gap-0.5 rounded-[10px] border border-white/[.12] bg-[#1f2024] p-1 shadow-[0_20px_44px_rgba(0,0,0,.45)]">
              {hidden.map((w) => {
                const tone = toneOf(w.tone);
                const Icon = w.icon;
                return (
                  <button key={w.id} type="button" onClick={() => activate(w)}
                    data-testid={w.testid ? `${w.testid}-restore` : undefined}
                    className="flex items-center gap-2 rounded-lg p-2 text-left transition-colors hover:bg-white/[.09]">
                    <span className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px]"
                      style={{ background: tone.bg, color: tone.fg }}>
                      {Icon && <Icon size={14} />}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-[#eceef2]">{w.title}</span>
                    {w.note && <span className="flex-none font-mono text-[11px] text-[#8a8f98]">{w.note}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** État d'une fenêtre. Fonctionne aussi hors fournisseur — repli local. */
function useWindowState(id, meta, onClose) {
  const ctx = useContext(FloatingCtx);
  const [local, setLocal] = useState(false);
  const { register, unregister, setHandlers } = ctx || {};
  const metaRef = useRef(meta);
  metaRef.current = meta;

  // Deux effets et non un seul : l'enregistrement ne dépend que de l'identité
  // de la fenêtre. Y mêler les métadonnées la désinscrirait puis réinscrirait
  // à chaque changement de titre — en fin de liste, et déployée.
  useEffect(() => {
    if (!register) return undefined;
    register(id, metaRef.current);
    return () => unregister(id);
  }, [id, register, unregister]);
  const { title, note, icon, tone, testid, closeTitle } = meta;
  useEffect(() => {
    if (register) register(id, { title, note, icon, tone, testid, closeTitle });
  }, [id, register, title, note, icon, tone, testid, closeTitle]);
  useEffect(() => { if (setHandlers) setHandlers(id, { onClose }); });

  if (!ctx) {
    return { minimized: local, dock: null,
             minimize: () => setLocal(true), restore: () => setLocal(false) };
  }
  const w = ctx.windows.find((x) => x.id === id);
  return {
    minimized: Boolean(w?.minimized),
    dock: ctx.dock,
    minimize: () => ctx.minimize(id),
    restore: () => ctx.restore(id),
  };
}

/** Pastille de reprise autonome — hors barre du bas (barre non montée,
 *  composant utilisé seul), la fenêtre réduite doit rester reprenable. */
function Pill({ icon: Icon, title, note, tone, onRestore, onClose, closeTitle, testid }) {
  const t = toneOf(tone);
  return (
    <div data-testid={testid ? `${testid}-pill` : undefined}
      className="fixed bottom-[calc(66px_+_var(--bottom-nav-h,0px))] left-1/2 z-40 flex max-w-[min(20rem,calc(100vw_-_1.5rem))] -translate-x-1/2 items-center gap-1 rounded-[10px] border border-white/[.12] bg-[rgba(22,23,26,.92)] p-1 shadow-[0_10px_30px_rgba(10,12,24,.28)]">
      <button type="button" onClick={onRestore} title={`Reprendre — ${title}`}
        data-testid={testid ? `${testid}-restore` : undefined}
        className="flex min-w-0 items-center gap-2 rounded-lg py-1 pl-1 pr-2 text-[13px] text-[#eceef2] hover:bg-white/10">
        <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md"
          style={{ background: t.bg, color: t.fg }}>
          {Icon && <Icon size={15} />}
        </span>
        <span className="min-w-0 truncate font-medium">{title}</span>
        {note && (
          <span className="min-w-0 max-w-[45%] shrink-[4] truncate font-mono text-[11px] text-[#9aa0ab]">{note}</span>
        )}
      </button>
      <button type="button" onClick={onClose} title={closeTitle || "Fermer"}
        aria-label={closeTitle || "Fermer"} className={DOCK_CLOSE_BTN}>
        <X size={13} />
      </button>
    </div>
  );
}

const HEADER_BTN = "inline-flex h-7 w-[34px] items-center justify-center rounded-md text-[#5b6270] transition-colors hover:bg-[rgba(15,23,42,.06)]";

export default function FloatingWindow({
  id, testid, icon: Icon, title, subtitle, note, aside, accent, tone,
  side = "center", width = 420, maxWidth = 880, restoreKey,
  onClose, closeTitle = "Fermer", footer, bodyRef, children,
}) {
  const resolvedTone = tone || toneFromAccent(accent);
  const { minimized, dock, minimize, restore } = useWindowState(
    id, { title, note, icon: Icon, tone: resolvedTone, testid, closeTitle }, onClose);
  const [grown, setGrown] = useState(false);
  // Position libre (après déplacement à la souris) ; `null` = ancrage par défaut.
  const [pos, setPos] = useState(null);
  const panelRef = useRef(null);

  // Une fenêtre rangée revient d'elle-même quand son sujet change — un nouvel
  // appel, une nouvelle préparation. Réduire vaut pour ce qu'on regardait, pas
  // pour tout ce qui suivra.
  useEffect(() => {
    if (restoreKey === undefined) return;
    restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoreKey]);

  // Une position bornée à l'écran d'avant peut être hors champ après un
  // redimensionnement : on revient à l'ancrage.
  useEffect(() => {
    const reset = () => setPos(null);
    window.addEventListener("resize", reset);
    return () => window.removeEventListener("resize", reset);
  }, []);

  const doMinimize = () => { setPos(null); minimize(); };
  const toggleGrow = () => { setPos(null); setGrown((v) => !v); };

  // Déplacement : saisie de l'en-tête, suivi du pointeur sur window (le
  // curseur peut sortir du panneau), position bornée à l'écran. Ignoré si la
  // cible est une commande, et sous 640 px où la fenêtre est une feuille
  // pleine largeur.
  const startDrag = (e) => {
    if (e.button != null && e.button !== 0) return;
    if (e.target.closest("button, a, input, select, textarea, [data-no-drag]")) return;
    if (typeof e.clientX !== "number" || window.innerWidth < 640) return;
    const panel = panelRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const dx = e.clientX - r.left;
    const dy = e.clientY - r.top;
    const move = (ev) => setPos({
      x: Math.min(Math.max(4, ev.clientX - dx), Math.max(4, window.innerWidth - r.width - 4)),
      y: Math.min(Math.max(4, ev.clientY - dy), Math.max(4, window.innerHeight - 60)),
    });
    const stop = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom, anciens navigateurs */ }
  };

  if (minimized) {
    // Dans la barre du bas, c'est elle qui dessine l'onglet.
    if (dock) return null;
    return (
      <Pill icon={Icon} title={title} note={note} tone={resolvedTone} testid={testid}
        onRestore={restore} onClose={onClose} closeTitle={closeTitle} />
    );
  }

  const t = toneOf(resolvedTone);
  // Ancrage par défaut : centré au-dessus de la barre. `side` garde une place
  // à gauche ou à droite pour que deux fenêtres (préparation + copilote)
  // cohabitent sur grand écran sans se recouvrir.
  const anchor = side === "left"
    ? "sm:right-auto sm:left-4 shell-sm:left-[calc(var(--sidebar-w,76px)_+_1rem)]"
    : side === "right"
      ? "sm:left-auto sm:right-4"
      : "sm:right-auto sm:left-1/2 sm:-translate-x-1/2";

  const body = (
    <div ref={panelRef} data-testid={testid}
      style={{
        "--fw-w": `${width}px`, "--fw-max": `${maxWidth}px`,
        ...(pos ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto", transform: "none" } : {}),
      }}
      className={cn(
        // Sous 640 px : feuille pleine largeur. Deux fenêtres ne se partagent
        // pas un écran de téléphone, elles s'y succèdent (règle du solo).
        // 66 px = barre du softphone (56 px, collée en bas) + 10 px de respiration,
        // plus la barre d'onglets mobile (--bottom-nav-h, posée par CrmLayout).
        "fixed bottom-[calc(66px_+_var(--bottom-nav-h,0px))] left-2 right-2 z-40 flex flex-col overflow-hidden rounded-xl",
        "border border-[rgba(15,23,42,.09)] bg-white shadow-[0_28px_70px_rgba(10,12,24,.22)] animate-fly-in",
        "max-h-[calc(100vh_-_9rem)]",
        grown
          ? "sm:w-[min(var(--fw-max),calc(100vw_-_2rem))] sm:max-h-[calc(100vh_-_7rem)]"
          : "sm:w-[min(var(--fw-w),calc(100vw_-_20px))]",
        anchor,
      )}>

      {/* EN-TÊTE — ne défile jamais : les commandes de la fenêtre doivent
          rester atteignables quel que soit le contenu. C'est aussi la poignée
          de déplacement. */}
      <div onPointerDown={startDrag} data-testid={testid ? `${testid}-header` : undefined}
        className="flex shrink-0 cursor-grab touch-none select-none items-center gap-2.5 border-b border-[#eef0f4] px-3 py-2.5 active:cursor-grabbing">
        <span className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px]"
          style={{ background: t.bg, color: t.fg }}>
          {Icon && <Icon size={14} />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-[#0b1524]">{title}</div>
          {subtitle && <div className="truncate text-[11.5px] text-[#8a90a3]">{subtitle}</div>}
        </div>
        {aside}
        <div className="flex shrink-0 items-center gap-0.5">
          <button type="button" onClick={doMinimize} title="Réduire" aria-label="Réduire la fenêtre"
            data-testid={testid ? `${testid}-minimize` : undefined} className={HEADER_BTN}>
            <Minus size={15} />
          </button>
          <button type="button" onClick={toggleGrow}
            title={grown ? "Réduire la taille" : "Agrandir"}
            aria-label={grown ? "Rendre la fenêtre à sa taille" : "Agrandir la fenêtre"}
            data-testid={testid ? `${testid}-grow` : undefined}
            className={cn(HEADER_BTN, "hidden sm:inline-flex")}>
            {grown ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
          <button type="button" onClick={onClose} title={closeTitle} aria-label={closeTitle}
            data-testid={testid ? `${testid}-close` : undefined}
            className={cn(HEADER_BTN, "hover:bg-[#e81123] hover:text-white")}>
            <X size={15} />
          </button>
        </div>
      </div>

      {/* CORPS — seul à défiler. `min-h-0` sans quoi un enfant long pousse la
          fenêtre au lieu de défiler à l'intérieur. */}
      <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {children}
      </div>

      {/* PIED — hors défilement, mais borné : un pied qui grandit (suggestion,
          compte rendu, commandes) ne doit pas écraser le corps ni déborder de
          la fenêtre. */}
      {footer && (
        <div className="max-h-[62%] shrink-0 overflow-y-auto border-t border-[#eef0f4]">
          {footer}
        </div>
      )}
    </div>
  );

  return typeof document === "undefined" ? body : createPortal(body, document.body);
}
